import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { useEffect } from "react";
import { Truck, ShieldCheck, Sparkles, Package } from "lucide-react";

import { getRelated, getGalleryImages } from "@/lib/pdp/product-helpers";
import { getCatalogRepository } from "@/lib/catalog/repository";
import { pushRecentlyViewed } from "@/lib/pdp/recently-viewed";
import { recordProductView } from "@/lib/admin/tracking/product-views";

import { Breadcrumb } from "@/components/pdp/Breadcrumb";
import { ProductGallery } from "@/components/pdp/ProductGallery";
import { PurchasePanel } from "@/components/pdp/PurchasePanel";
import { InfoAccordion } from "@/components/pdp/InfoAccordion";
import { RelatedProducts } from "@/components/pdp/RelatedProducts";
import { RecentlyViewed } from "@/components/pdp/RecentlyViewed";
import { fallbackProductImage } from "@/lib/catalog/fallback-images";
import { DEFAULT_PRODUCT_BENEFITS } from "@/lib/catalog/products";
import { canonicalUrl } from "@/lib/seo/site";
import {
  breadcrumbSchema,
  ldScript,
  productImageAlt,
  productSchema,
  productSchemaImages,
  productSeoDescription,
  productSeoTitle,
} from "@/lib/seo/schema";


export const Route = createFileRoute("/fragrances/$slug")({
  loader: async ({ params }) => {
    const product = await getCatalogRepository().getProduct(params.slug);
    if (!product) throw notFound();
    const repo = getCatalogRepository();
    const [collections, allProducts] = await Promise.all([repo.getCollections(), repo.getProducts()]);
    return { product, collections, allProducts };
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: "Fragrance not found — CB16 Maison" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const { product, collections } = loaderData;
    const collection = collections.find((c: { id: string }) => c.id === product.collectionId);
    const title = productSeoTitle(product);
    const description = productSeoDescription(product);
    const url = canonicalUrl(`/fragrances/${params.slug}`);
    const image = productSchemaImages(product)[0];
    const crumbs = [
      { name: "Home", path: "/" },
      { name: "Fragrances", path: "/fragrances" },
      ...(collection ? [{ name: collection.name, path: `/collections/${collection.id}` }] : []),
      { name: product.name, path: `/fragrances/${params.slug}` },
    ];
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "product" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        ...(image
          ? [
              { property: "og:image", content: image },
              { property: "og:image:alt", content: productImageAlt(product.name) },
              { name: "twitter:image", content: image },
            ]
          : []),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [ldScript(productSchema(product)), ldScript(breadcrumbSchema(crumbs))],
    };
  },

  component: ProductDetailPage,
  notFoundComponent: ProductNotFound,
  pendingComponent: PdpSkeleton,
  errorComponent: PdpError,
});

function ProductDetailPage() {
  const { product, collections, allProducts } = Route.useLoaderData();
  const collection = collections.find((c: { id: string }) => c.id === product.collectionId);
  const images = getGalleryImages(product);
  const related = getRelated(product, allProducts, 4);

  useEffect(() => {
    pushRecentlyViewed(product.slug);
    recordProductView({ productId: product.id, slug: product.slug, name: product.name });
  }, [product.slug]);

  return (
    <div className="bg-white" style={{ fontFamily: "'Inter', sans-serif" }}>
      <div className="mx-auto max-w-7xl px-6 pt-8 lg:px-10 lg:pt-10">
        <Breadcrumb
          crumbs={[
            { label: "Home", to: "/" },
            { label: "Fragrances", to: "/fragrances" },
            ...(collection ? [{ label: collection.name, to: `/collections/${collection.id}` }] : []),
            { label: product.name },
          ]}
        />
      </div>

      <section className="mx-auto max-w-7xl px-6 py-10 lg:grid lg:grid-cols-2 lg:gap-16 lg:px-10 lg:py-16">
        <div>
          <ProductGallery
            images={images}
            name={product.name}
            badge={product.badge}
            fallback={fallbackProductImage(product.slug, product.categoryId)}
          />
        </div>

        <div className="mt-10 flex flex-col gap-8 lg:mt-0">
          <header>
            <span className="text-[11px] font-medium uppercase tracking-[0.32em] text-[#C6A15B]">
              {collection?.name ?? product.family} · {product.gender}
            </span>
            <h1
              className="mt-4 text-4xl font-normal leading-tight text-black sm:text-5xl"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {product.name}
            </h1>
            <div className="mt-5 h-px w-10 bg-[#C6A15B]" />
            <p className="mt-6 max-w-lg text-sm leading-relaxed text-black/60 sm:text-base">
              A {product.family.toLowerCase()} {product.concentration} composed around{" "}
              {product.notes}. Hand-blended in small batches at the CB16 atelier in Mumbai, India.
            </p>
          </header>

          <PurchasePanel product={product} />

          {(() => {
            const benefits = (product.benefits ?? DEFAULT_PRODUCT_BENEFITS).filter(
              (b) => b.enabled && b.text.trim().length > 0,
            );
            if (benefits.length === 0) return null;
            const icons = [Sparkles, Package, Truck, ShieldCheck];
            return (
              <ul className="grid grid-cols-2 gap-4 border-t border-black/10 pt-6 text-xs text-black/70">
                {benefits.map((benefit, i) => {
                  const Icon = icons[i % icons.length];
                  return (
                    <li key={i} className="flex items-start gap-3">
                      <Icon className="mt-0.5 h-4 w-4 text-[#C6A15B]" aria-hidden />
                      <span>{benefit.text}</span>
                    </li>
                  );
                })}
              </ul>
            );
          })()}

          <InfoAccordion product={product} />
        </div>
      </section>

      <RelatedProducts eyebrow="You May Also Love" title="Related Fragrances" products={related} />
      <RecentlyViewed excludeSlug={product.slug} catalog={allProducts} />
    </div>
  );
}

function PdpSkeleton() {
  return (
    <div className="mx-auto max-w-7xl animate-pulse px-6 py-16 lg:grid lg:grid-cols-2 lg:gap-16 lg:px-10">
      <div className="aspect-[4/5] bg-[#faf7f2]" />
      <div className="mt-10 space-y-6 lg:mt-0">
        <div className="h-3 w-32 bg-black/10" />
        <div className="h-10 w-3/4 bg-black/10" />
        <div className="h-px w-10 bg-[#C6A15B]" />
        <div className="h-3 w-full bg-black/10" />
        <div className="h-3 w-5/6 bg-black/10" />
        <div className="h-12 w-40 bg-black/10" />
        <div className="h-14 w-full bg-black/10" />
      </div>
    </div>
  );
}

function PdpError({ reset }: { reset: () => void }) {
  return (
    <div className="mx-auto flex min-h-[60vh] max-w-xl flex-col items-center justify-center px-6 py-24 text-center">
      <span className="text-[11px] font-medium uppercase tracking-[0.32em] text-[#C6A15B]">
        Something went wrong
      </span>
      <h1
        className="mt-4 text-3xl font-normal text-black"
        style={{ fontFamily: "'Playfair Display', serif" }}
      >
        This flacon didn't load
      </h1>
      <p className="mt-4 text-sm text-black/60">Please try again in a moment.</p>
      <button
        type="button"
        onClick={reset}
        className="mt-8 border border-black px-8 py-3 text-[11px] font-medium uppercase tracking-[0.28em] text-black transition hover:border-[#C6A15B] hover:text-[#C6A15B]"
      >
        Retry
      </button>
    </div>
  );
}

function ProductNotFound() {
  return (
    <div className="mx-auto flex min-h-[60vh] max-w-xl flex-col items-center justify-center px-6 py-24 text-center">
      <span className="text-[11px] font-medium uppercase tracking-[0.32em] text-[#C6A15B]">
        404
      </span>
      <h1
        className="mt-4 text-3xl font-normal text-black"
        style={{ fontFamily: "'Playfair Display', serif" }}
      >
        Fragrance not found
      </h1>
      <p className="mt-4 text-sm text-black/60">
        This composition may have been archived. Browse the full catalog to find your signature.
      </p>
      <Link
        to="/fragrances"
        className="mt-8 border border-black px-8 py-3 text-[11px] font-medium uppercase tracking-[0.28em] text-black transition hover:border-[#C6A15B] hover:text-[#C6A15B]"
      >
        Browse Fragrances
      </Link>
    </div>
  );
}
